import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Products', path: '/products' },
    { name: 'FAQs', path: '/faqs' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact Us', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center text-sm">
            <div className="flex gap-6">
              <a href="tel:954-495-2929" className="hover:text-[#C9A361] transition-colors flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Call us
              </a>
              <a href="mailto:contact@progressinsure.com" className="hover:text-[#C9A361] transition-colors flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_insure-your-future/artifacts/9q3kkyfz_Progress-Logo-Blk-Gld-WEB%20%281%29.png" 
                alt="Progress Insurance Solutions Logo"
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-colors hover:text-[#C9A361] ${
                    isActive(item.path) ? 'text-[#C9A361]' : 'text-slate-700'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* CTA Box */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="bg-[#C9A361] px-6 py-3 rounded-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-white" />
                <a href="tel:954-495-2929" className="text-lg font-bold text-white hover:text-slate-900 transition-colors">
                  954-495-2929
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`block px-4 py-2 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#F5F1E8] text-[#C9A361]'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="mt-4 p-4 bg-[#C9A361] rounded-lg text-center">
                <a href="tel:954-495-2929" className="text-lg font-bold text-white">
                  954-495-2929
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <footer className="bg-slate-50 text-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Company Info */}
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_insure-your-future/artifacts/9q3kkyfz_Progress-Logo-Blk-Gld-WEB%20%281%29.png" 
                  alt="Progress Insurance Solutions Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-slate-600 text-sm mb-4">
                Independent insurance agency providing comprehensive coverage solutions for individuals and businesses.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="text-slate-600 hover:text-[#C9A361] transition-colors text-sm">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Commercial Insurance Products */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Commercial Insurance</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/products/general-liability" className="text-slate-600 hover:text-[#C9A361] transition-colors">General Liability</Link></li>
                <li><Link to="/products/commercial-property" className="text-slate-600 hover:text-[#C9A361] transition-colors">Commercial Property</Link></li>
                <li><Link to="/products/workers-compensation" className="text-slate-600 hover:text-[#C9A361] transition-colors">Workers' Compensation</Link></li>
                <li><Link to="/products/commercial-auto" className="text-slate-600 hover:text-[#C9A361] transition-colors">Commercial Auto</Link></li>
                <li><Link to="/products/professional-liability" className="text-slate-600 hover:text-[#C9A361] transition-colors">Professional Liability</Link></li>
                <li><Link to="/products/cyber-risk" className="text-slate-600 hover:text-[#C9A361] transition-colors">Cyber & Specialty Risk</Link></li>
              </ul>
            </div>

            {/* Personal Insurance Products */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Personal Insurance</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/products/auto" className="text-slate-600 hover:text-[#C9A361] transition-colors">Auto Insurance</Link></li>
                <li><Link to="/products/homeowners" className="text-slate-600 hover:text-[#C9A361] transition-colors">Homeowners & Renters</Link></li>
                <li><Link to="/products/flood" className="text-slate-600 hover:text-[#C9A361] transition-colors">Flood Insurance</Link></li>
                <li><Link to="/products/umbrella" className="text-slate-600 hover:text-[#C9A361] transition-colors">Umbrella Protection</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-700">Coral Springs Office</p>
                    <p>3111 N University Dr Ste 404, Coral Springs, FL 33065</p>
                  </div>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-700">Maitland Office</p>
                    <p>500 N Maitland Ave Ste 304, Maitland, FL 32751</p>
                  </div>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <a href="tel:954-495-2929" className="hover:text-[#C9A361] transition-colors">
                    954-495-2929
                  </a>
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <a href="mailto:contact@progressinsure.com" className="hover:text-[#C9A361] transition-colors">
                    contact@progressinsure.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-200 mt-8 pt-8 text-center text-sm text-slate-600">
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4">
              <Link to="/privacy-policy" className="hover:text-[#C9A361] transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-[#C9A361] transition-colors">Terms of Service</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} Progress Insurance Solutions. All rights reserved.</p>
            <p className="mt-2">
              Design & Developed by{' '}
              <a 
                href="https://www.dplus.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#C9A361] hover:text-[#B8925A] font-semibold transition-colors"
              >
                Design Plus
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
